import { SyncState, VisitOutcome } from '@axle-health/graphql-schema';

import { ELEANOR_CONTAINER_KEYS } from '../containerKeys';
import { date } from '../graphInputHelpers';
import { QueueDefinition, RootEntity } from '../types';

/**
 * Eleanor Health treats substance use disorder. Its queues are keyed on myUnity
 * appointment type IDs.
 *
 * Every queue is declared in full here, including the ones whose filters happen to
 * match another org's. A queue is per-org configuration, so changing what this org
 * counts means editing this file and no other.
 */

// MSUD intake visits that still need a nursing appointment, and whose patient has
// no nursing intake visit at all.
const RN_POSITION_NEEDED: QueueDefinition<RootEntity.VISIT> = {
    key: ELEANOR_CONTAINER_KEYS.rnPositionNeeded,
    label: 'MSUD Intake Visit Needs Nursing',
    rootEntity: RootEntity.VISIT,
    snoozable: false,
    heavyQuery: false,
    buildFilters: () => ({
        services: {
            externalIds: {
                type: { exact: 'APPOINTMENT_TYPE_ID' },
                value: { exact: '593' },
            },
        },
        outcome: { exact: VisitOutcome.NONE },
        AND: {
            NOT: {
                patient: {
                    visits: {
                        services: { name: { iContains: 'Nursing Intake' } },
                    },
                },
            },
        },
    }),
};

// Visits still waiting on a clinician.
const UNASSIGNED: QueueDefinition<RootEntity.VISIT> = {
    key: ELEANOR_CONTAINER_KEYS.unassigned,
    label: 'Unassigned',
    rootEntity: RootEntity.VISIT,
    snoozable: false,
    heavyQuery: false,
    buildFilters: () => ({
        needsClinicians: { exact: true },
        outcome: { exact: VisitOutcome.NONE },
    }),
};

// Assigned future visits where the clinician's payer does not match the patient's,
// excluding those a scheduler has explicitly marked as ignorable.
const RN_PATIENT_PAYER_MISMATCH: QueueDefinition<RootEntity.VISIT> = {
    key: ELEANOR_CONTAINER_KEYS.rnPatientPayerMismatch,
    label: 'Payer Mismatch',
    rootEntity: RootEntity.VISIT,
    snoozable: false,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        clinicianAppointments: {
            clinician: { id: { isNull: false } },
            arePatientClinicianPayersCompatible: { exact: false },
        },
        effectiveDate: { gte: date(now, timezone) },
        outcome: { exact: VisitOutcome.NONE },
        NOT: {
            notesForDashboard: { iContains: 'axle-ignore-payer-mismatch' },
        },
    }),
};

// Pending visits whose clinician appointment failed to reach the EMR.
const VISIT_CLINICIAN_APPOINTMENT_SYNC_ISSUES: QueueDefinition<RootEntity.VISIT> = {
    key: ELEANOR_CONTAINER_KEYS.visitClinicianAppointmentSyncIssues,
    label: 'Appointment Sync Issues',
    rootEntity: RootEntity.VISIT,
    snoozable: false,
    heavyQuery: true,
    buildFilters: () => ({
        clinicianAppointments: { syncStatus: { state: { exact: SyncState.FAILING } } },
        outcome: { exact: VisitOutcome.NONE },
    }),
};

// Patients that never reached the EMR, so they carry no external ID.
const UNSYNCED_PATIENTS: QueueDefinition<RootEntity.PATIENT> = {
    key: ELEANOR_CONTAINER_KEYS.unsyncedPatients,
    label: 'Patient Sync Issues',
    rootEntity: RootEntity.PATIENT,
    snoozable: false,
    heavyQuery: false,
    buildFilters: () => ({
        externalIds: { value: { isNull: true } },
    }),
};

export const ELEANOR_QUEUES: QueueDefinition[] = [
    RN_POSITION_NEEDED,
    UNASSIGNED,
    RN_PATIENT_PAYER_MISMATCH,
    VISIT_CLINICIAN_APPOINTMENT_SYNC_ISSUES,
    UNSYNCED_PATIENTS,
];
